import { createContext, useContext } from 'react';
import type { UseLiveVideoReturn } from '../hooks/useLiveVideo';
import type { UseLiveChatReturn } from '../hooks/useLiveChat';

/**
 * The one live session of a consultation — one Twilio room, one conversation —
 * held above every screen that talks to it. LiveSessionProvider builds it.
 *
 * Before this, `useLiveVideo()` built a `TwilioVideoProvider` of its own on every
 * call and tore it down in the effect cleanup. TransitionScreen and
 * ConsultationScreen each called it, so the customer joined the room on the
 * transition screen, left it the moment that screen unmounted, and joined again
 * from the consultation. With the seller already waiting, that reads on their side
 * as the customer's video appearing, vanishing, and coming back.
 *
 * The room now outlives the screen change. Its lifetime is the provider's: it ends
 * when the customer ends the call (ConsultationScreen.handleEndCall disconnects
 * both legs explicitly) or when the tab tears the App tree down.
 */
export interface LiveSession {
  video: UseLiveVideoReturn;
  chat: UseLiveChatReturn;
}

export const LiveSessionContext = createContext<LiveSession | null>(null);

function useLiveSession(): LiveSession {
  const session = useContext(LiveSessionContext);
  if (!session) {
    throw new Error('useLiveSession must be used inside <LiveSessionProvider>');
  }
  return session;
}

/** The shared video room. Same object for every screen inside the provider. */
export function useVideoSession(): UseLiveVideoReturn {
  return useLiveSession().video;
}

/** The shared chat conversation. Same object for every screen inside the provider. */
export function useChatSession(): UseLiveChatReturn {
  return useLiveSession().chat;
}
